import { useIntl } from "react-intl"
import { Tuple } from "components/Shared/Interfaces"
import { isFeatureEnabled } from "config/feature-flags"
import {
	METRIC_AVG_PRICE_SELECT_FEATURE_FLAG_ID,
	METRIC_AVG_SPEND_SELECT_FEATURE_FLAG_ID,
	METRIC_AVG_TICKET_SELECT_FEATURE_FLAG_ID,
	METRIC_CLIENTS_SELECT_FEATURE_FLAG_ID,
	METRIC_ITEMS_PER_VISIT_SELECT_FEATURE_FLAG_ID,
	METRIC_PENETRATION_SELECT_FEATURE_FLAG_ID,
	METRIC_PURCHASE_FRECUENCY_SELECT_FEATURE_FLAG_ID,
	METRIC_SALES_SELECT_FEATURE_FLAG_ID,
	METRIC_STORES_SELLING_SELECT_FEATURE_FLAG_ID,
	METRIC_TRANSACTIONS_SELECT_FEATURE_FLAG_ID,
	METRIC_UNITS_SELECT_FEATURE_FLAG_ID,
} from "components/Transactional/Performance/constants"
import { COLOR_PALETTE } from "colors"

export type MetricColors = typeof PRIMARY_COLOR | typeof SECONDARY_COLOR
export const PRIMARY_COLOR = COLOR_PALETTE.PURPLE_500
export const SECONDARY_COLOR = COLOR_PALETTE.YELLOW_600
export const DISABLED_COLOR = COLOR_PALETTE.DISABLED_COLOR

export const NON_OPTION = "no_option"
export const SALES = "sales"
export const UNITS = "units"
export const TRANSACTIONS = "transactions"
export const CLIENTS = "clients"
export const WEIGHTED_PENETRATION_CLIENTS = "weighted_clients"
export const WEIGHTED_PENETRATION_TRANSACTIONS = "weighted_transactions"
export const TOTAL_PENETRATION_CLIENTS = "total_clients"
export const TOTAL_PENETRATION_TRANSACTIONS = "total_transactions"
export const PURCHASE_FREQUENCY = "purchase_frequency"
export const AVG_TICKET = "average_ticket"
export const AVG_SPEND = "average_spend"
export const AVG_PRICE = "average_price_per_item"
export const ITEMS_PER_VISIT = "items_per_visit"
export const STORES_SELLING = "stores_selling"

interface TupleWithVisibility extends Tuple {
	isVisible: boolean
}

export const DEFAULT_TIMELINE_RESPONSE = {
	commercialBenchmarkTimeline: {
		data: [],
	},
}

export const DEFAULT_GROWTH_TIMELINE_RESPONSE = {
	timelineGrowth: {
		data: [],
	},
}

export const SELECTED_METRIC: Record<string, string> = {
	[NON_OPTION]: "no-option",
	[SALES]: "sales",
	[UNITS]: "units",
	[TRANSACTIONS]: "transactions",
	[CLIENTS]: "clients",
	[WEIGHTED_PENETRATION_CLIENTS]: "penetration",
	[WEIGHTED_PENETRATION_TRANSACTIONS]: "penetration",
	[TOTAL_PENETRATION_CLIENTS]: "penetration",
	[TOTAL_PENETRATION_TRANSACTIONS]: "penetration",
	[PURCHASE_FREQUENCY]: "purchase-frequency",
	[AVG_TICKET]: "average-ticket",
	[AVG_SPEND]: "average-spend",
	[AVG_PRICE]: "average-price-per-item",
	[ITEMS_PER_VISIT]: "items-per-visit",
	[STORES_SELLING]: "stores-selling",
}

export const METRIC: Record<string, string> = {
	[NON_OPTION]: "no-option_timeline",
	[SALES]: "sales_timeline",
	[UNITS]: "units_timeline",
	[TRANSACTIONS]: "transactions_timeline",
	[CLIENTS]: "clients_timeline",
	[WEIGHTED_PENETRATION_CLIENTS]: "clients_timeline_weighted_penetration",
	[WEIGHTED_PENETRATION_TRANSACTIONS]:
		"transactions_timeline_weighted_penetration",
	[TOTAL_PENETRATION_CLIENTS]: "clients_timeline_total_penetration",
	[TOTAL_PENETRATION_TRANSACTIONS]: "transactions_timeline_total_penetration",
	[PURCHASE_FREQUENCY]: "purchase-frequency_timeline",
	[AVG_TICKET]: "average-ticket_timeline",
	[AVG_SPEND]: "average-spend_timeline",
	[AVG_PRICE]: "average-price-per-item_timeline",
	[ITEMS_PER_VISIT]: "items-per-visit_timeline",
	[STORES_SELLING]: "stores-selling_timeline",
}

export const METRIC_MAPPER: Record<string, string> = {
	sales: SALES,
	units: UNITS,
	transactions: TRANSACTIONS,
	clients: CLIENTS,
	weighted_clients: WEIGHTED_PENETRATION_CLIENTS,
	weighted_transactions: WEIGHTED_PENETRATION_TRANSACTIONS,
	total_clients: TOTAL_PENETRATION_CLIENTS,
	total_transactions: TOTAL_PENETRATION_TRANSACTIONS,
	purchase_frequency: PURCHASE_FREQUENCY,
	average_ticket: AVG_TICKET,
	average_spend: AVG_SPEND,
	average_price_per_item: AVG_PRICE,
	items_per_visit: ITEMS_PER_VISIT,
	stores_selling: STORES_SELLING,
}

export const options = (): TupleWithVisibility[] => {
	const intl = useIntl()
	const id =
		"phronesis.transactional.application.commercial.benchmark.timeline.metricsOptions"
	return [
		{
			code: NON_OPTION,
			name: intl.formatMessage({ id }, { metric: NON_OPTION }),
			isVisible: true,
		},
		{
			code: SALES,
			name: intl.formatMessage({ id }, { metric: SALES }),
			isVisible: isFeatureEnabled(METRIC_SALES_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: UNITS,
			name: intl.formatMessage({ id }, { metric: UNITS }),
			isVisible: isFeatureEnabled(METRIC_UNITS_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: TRANSACTIONS,
			name: intl.formatMessage({ id }, { metric: TRANSACTIONS }),
			isVisible: isFeatureEnabled(METRIC_TRANSACTIONS_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: CLIENTS,
			name: intl.formatMessage({ id }, { metric: CLIENTS }),
			isVisible: isFeatureEnabled(METRIC_CLIENTS_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: WEIGHTED_PENETRATION_CLIENTS,
			name: intl.formatMessage(
				{ id },
				{ metric: WEIGHTED_PENETRATION_CLIENTS }
			),
			isVisible: isFeatureEnabled(METRIC_PENETRATION_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: WEIGHTED_PENETRATION_TRANSACTIONS,
			name: intl.formatMessage(
				{ id },
				{ metric: WEIGHTED_PENETRATION_TRANSACTIONS }
			),
			isVisible: isFeatureEnabled(METRIC_PENETRATION_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: TOTAL_PENETRATION_CLIENTS,
			name: intl.formatMessage({ id }, { metric: TOTAL_PENETRATION_CLIENTS }),
			isVisible: isFeatureEnabled(METRIC_PENETRATION_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: TOTAL_PENETRATION_TRANSACTIONS,
			name: intl.formatMessage(
				{ id },
				{ metric: TOTAL_PENETRATION_TRANSACTIONS }
			),
			isVisible: isFeatureEnabled(METRIC_PENETRATION_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: PURCHASE_FREQUENCY,
			name: intl.formatMessage({ id }, { metric: PURCHASE_FREQUENCY }),
			isVisible: isFeatureEnabled(
				METRIC_PURCHASE_FRECUENCY_SELECT_FEATURE_FLAG_ID
			),
		},
		{
			code: AVG_TICKET,
			name: intl.formatMessage({ id }, { metric: AVG_TICKET }),
			isVisible: isFeatureEnabled(METRIC_AVG_TICKET_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: AVG_SPEND,
			name: intl.formatMessage({ id }, { metric: AVG_SPEND }),
			isVisible: isFeatureEnabled(METRIC_AVG_SPEND_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: AVG_PRICE,
			name: intl.formatMessage({ id }, { metric: AVG_PRICE }),
			isVisible: isFeatureEnabled(METRIC_AVG_PRICE_SELECT_FEATURE_FLAG_ID),
		},
		{
			code: ITEMS_PER_VISIT,
			name: intl.formatMessage({ id }, { metric: ITEMS_PER_VISIT }),
			isVisible: isFeatureEnabled(
				METRIC_ITEMS_PER_VISIT_SELECT_FEATURE_FLAG_ID
			),
		},
		{
			code: STORES_SELLING,
			name: intl.formatMessage({ id }, { metric: STORES_SELLING }),
			isVisible: isFeatureEnabled(METRIC_STORES_SELLING_SELECT_FEATURE_FLAG_ID),
		},
	]
}
